import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {FormPageComponent} from '../../../components/form-page/form-page.component';
import {Router} from '@angular/router';
import {UserService, WaitResult} from '../../../services/user.service';
import {ButtonDirective, ButtonLabel} from 'primeng/button';
import {AsyncPipe, Location} from '@angular/common';
import {ProgressSpinner} from 'primeng/progressspinner';
import {AnalyticsService} from '../../../services/analytics.service';
import {PaymentService} from '../../../services/payment/payment.service';
import {ActivatedRoute} from '@angular/router';
import typia from "typia";
import {MessageService} from "primeng/api";
import {tap} from "rxjs";
import {claimFirstVisit} from '../claim-first-visit';
import {readStripeCheckoutReturn} from '../stripe-checkout-return';

@Component({
  selector: 'homey-resubscribe-payment-success-page',
  standalone: true,
  imports: [
    FormPageComponent,
    ButtonDirective,
    ButtonLabel,
    AsyncPipe,
    ProgressSpinner,
  ],
  templateUrl: './resubscribe-payment-success-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResubscribePaymentSuccessPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly paymentService = inject(PaymentService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly location = inject(Location);
  private readonly isInterval = typia.createIs<'annual' | 'monthly'>();

  protected readonly completed$ = this.userService.waitUntilPaymentStatusPaid().pipe(
    tap(result => this.handleWaitResult(result)),
  );

  constructor(private readonly userService: UserService) {
    const intervalFromQuery = this.route.snapshot.queryParamMap.get('interval');
    const interval = this.isInterval(intervalFromQuery) ? intervalFromQuery : 'unknown';
    // Reload and Back come through here again: count the payment on the first visit only.
    if (claimFirstVisit(this.location)) {
      void this.analyticsService.sendPaymentSuccess({
        flow: 'resubscribe',
        checkoutLocation: 'default',
        interval,
        checkout: readStripeCheckoutReturn(this.route.snapshot.queryParamMap),
      });
    }
  }

  protected readonly user = this.userService.user;

  protected readonly onContinue = async () => {
    await this.paymentService.runAutoApplyAfterResubscribe();
    void this.router.navigateByUrl('/');
  };

  private handleWaitResult(result: WaitResult) {
    if (result === 'unauthorized') {
      this.messageService.add({severity: 'warn', summary: 'Session expired', detail: 'Please log in again.'});
      void this.router.navigateByUrl('/login');
    } else if (result === 'timed-out') {
      this.messageService.add({severity: 'warn', summary: 'Timed out', detail: 'Redirected back to home.'});
      void this.router.navigateByUrl('/');
    }
  }
}
